import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import { api } from '@/api/client'

const nullableString = z.string().nullable().optional()

const contextItemSchema = z
  .object({
    id: z.number().optional(),
    name: nullableString,
    logo_url: nullableString,
  })
  .passthrough()

const contextSchema = z
  .object({
    poule: contextItemSchema.nullable().optional(),
    season: contextItemSchema.nullable().optional(),
    fantasy_league: contextItemSchema.nullable().optional(),
    fantasy_subleague: contextItemSchema.nullable().optional(),
    fantasy_challenge: contextItemSchema.nullable().optional(),
    app_round: contextItemSchema.nullable().optional(),
    participant_count: z.number().nullable().optional(),
  })
  .passthrough()

const honorSchema = z
  .object({
    id: z.number(),
    game_mode: z.string(),
    honor_type: z.string(),
    title: z.string(),
    description: nullableString,
    rank: z.number().nullable().optional(),
    points: z.number().nullable().optional(),
    participant_count: z.number().nullable().optional(),
    metadata: z.record(z.string(), z.unknown()).optional().default({}),
    achieved_at: nullableString,
    context: contextSchema.optional().default({}),
  })
  .passthrough()

const recordSchema = z
  .object({
    id: z.number(),
    game_mode: z.string(),
    record_type: z.string(),
    value: z.union([z.number(), z.string()]).nullable().optional(),
    value_label: nullableString,
    metadata: z.record(z.string(), z.unknown()).optional().default({}),
    achieved_at: nullableString,
    context: contextSchema.optional().default({}),
  })
  .passthrough()

const participationSchema = z
  .object({
    id: z.number(),
    name: z.string(),
    classification: nullableString,
    thumbnail_url: nullableString,
    season: contextItemSchema.nullable().optional(),
    final_rank: z.number().nullable().optional(),
    final_points: z.number().nullable().optional(),
    participant_count: z.number().nullable().optional(),
    membership: z
      .object({
        joined_at: nullableString,
      })
      .nullable()
      .optional(),
  })
  .passthrough()

const sectionSchema = z
  .object({
    honors: z.array(honorSchema).optional().default([]),
    participations: z.array(participationSchema).optional().default([]),
    records: z.array(recordSchema).optional().default([]),
  })
  .passthrough()

const trophyCabinetSchema = z
  .object({
    data: z.object({
      summary: z
        .object({
          honors_count: z.number().optional().default(0),
          records_count: z.number().optional().default(0),
        })
        .optional()
        .default({}),
      football_poule: sectionSchema.optional().default({}),
      golero_manager: sectionSchema.optional().default({}),
      trivia: sectionSchema.optional().default({}),
    }),
    last_updated: nullableString,
    source_version: nullableString,
  })
  .passthrough()

export type TrophyContext = z.infer<typeof contextSchema>
export type TrophyHonor = z.infer<typeof honorSchema>
export type TrophyRecord = z.infer<typeof recordSchema>
export type TrophyParticipation = z.infer<typeof participationSchema>
export type TrophySection = z.infer<typeof sectionSchema>
export type TrophyCabinet = z.infer<typeof trophyCabinetSchema>

async function fetchTrophyCabinet(): Promise<TrophyCabinet> {
  const response = await api.get<unknown>('/users/me/trophy-cabinet')
  const parsed = trophyCabinetSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[trophy-cabinet] Invalid payload', parsed.error.flatten())
    throw new Error('De prijzenkast kon niet worden geladen.')
  }

  return parsed.data
}

export function useTrophyCabinet(enabled = true) {
  return useQuery({
    queryKey: ['trophy-cabinet'],
    queryFn: fetchTrophyCabinet,
    enabled,
    staleTime: 60_000,
  })
}
